/**
 * Doctor Dashboard — queue overview, emergencies, and quick actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doctorAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const DoctorDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await doctorAPI.getDashboard();
      setDashboard(res.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
      toast.error(err?.message || 'Unable to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Live updates: refresh on new queue entry or emergency
  const { on } = useNotifications();
  useEffect(() => on('queue_update', loadDashboard), [on, loadDashboard]);
  useEffect(() => on('emergency_request', loadDashboard), [on, loadDashboard]);
  // Remove claimed emergency from state immediately without a full re-fetch
  useEffect(() => on('emergency_claimed', ({ appointment_id }) => {
    setDashboard(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        emergencies: (prev.emergencies || []).filter(e => e.id !== appointment_id),
      };
    });
  }), [on]);

  const firstName = (userProfile?.full_name || 'Doctor').split(' ')[0];

  if (loading) {
    return (
      <Layout title="Dashboard" role="doctor">
        <div className="space-y-4 p-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  const queue = dashboard?.patient_queue || [];
  const emergencies = dashboard?.emergencies || [];
  const recentConsultations = dashboard?.recent_consultations || [];
  const openConsultation = (patient) => {
    if (patient?.room_id) {
      navigate(`/consultation?room=${patient.room_id}&appointment=${patient.id}`);
      return;
    }

    navigate(`/doctor/consultation/start?appointment=${patient.id}`);
  };

  return (
    <Layout title="Dashboard" role="doctor">
      <div className="p-4 space-y-5 pb-24">
        {/* Greeting */}
        <div className="bg-gradient-to-r from-accent-500 to-green-600 rounded-2xl p-5 text-white">
          <p className="text-green-100 text-sm">Good day,</p>
          <h2 className="text-xl font-bold">Dr. {firstName} 👨‍⚕️</h2>
          <div className="flex items-center gap-4 mt-2 text-sm text-green-100">
            <span>{queue.length} patients in queue</span>
            <span>•</span>
            <span>{recentConsultations.length} consultations today</span>
          </div>
        </div>

        {/* Emergency Alerts */}
        {emergencies.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <h3 className="font-bold text-red-800">Emergency Requests ({emergencies.length})</h3>
            </div>
            {emergencies.slice(0, 2).map(em => (
              <div key={em.id} className="bg-white rounded-xl p-3 mb-2 last:mb-0">
                <p className="text-sm font-medium text-gray-900">{em.patient_name || 'Patient'}</p>
                <p className="text-xs text-gray-500 mt-0.5">{em.symptoms || 'Emergency consultation needed'}</p>
                <button
                  onClick={() => navigate(`/doctor/accept-emergency?id=${em.id}`)}
                  className="mt-2 px-4 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                >
                  Accept Emergency
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="card text-center py-4">
            <p className="text-2xl font-bold text-accent-600">{queue.length}</p>
            <p className="text-xs text-gray-500 mt-1">In Queue</p>
          </div>
          <div className="card text-center py-4">
            <p className="text-2xl font-bold text-primary-600">{recentConsultations.length}</p>
            <p className="text-xs text-gray-500 mt-1">Today</p>
          </div>
          <div className="card text-center py-4">
            <p className="text-2xl font-bold text-red-500">{emergencies.length}</p>
            <p className="text-xs text-gray-500 mt-1">Emergencies</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate('/doctor/queue')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="text-sm font-medium">Patient Queue</span>
          </button>

          <button onClick={() => navigate('/doctor/schedule')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-sm font-medium">My Schedule</span>
          </button>

          <button onClick={() => navigate('/doctor/consultations')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-sm font-medium">Consultations</span>
          </button>

          <button onClick={() => navigate('/doctor/prescriptions')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="text-sm font-medium">Prescriptions</span>
          </button>
        </div>

        {/* Patient Queue */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Patient Queue</h3>
            <Link to="/doctor/queue" className="text-accent-500 text-sm font-medium">View All</Link>
          </div>

          {queue.length === 0 ? (
            <div className="card text-center py-8 text-gray-400">
              <p className="text-sm">No patients in queue</p>
            </div>
          ) : (
            <div className="space-y-3">
              {queue.slice(0, 4).map((patient, idx) => (
                <div key={patient.id} className="card flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    idx === 0 ? 'bg-accent-500' : 'bg-gray-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{patient.patient_name || 'Patient'}</p>
                    <p className="text-xs text-gray-500">
                      {patient.appointment_type === 'follow_up' ? 'Follow-up Visit'
                        : patient.appointment_type === 'emergency' ? 'Emergency'
                        : patient.reason || 'Regular Visit'}
                    </p>
                  </div>
                  {patient.appointment_type === 'emergency' || patient.type === 'emergency' ? (
                    <span className="badge-danger text-xs">Emergency</span>
                  ) : patient.payment_status !== 'paid' ? (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">Unpaid</span>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/doctor/patients/${patient.patient_uid}`)}
                      className="px-3 py-1.5 bg-white text-gray-700 text-xs font-medium rounded-lg border border-gray-200"
                    >
                      Profile
                    </button>
                    {idx === 0 && (
                      <button
                        onClick={() => openConsultation(patient)}
                        className="px-3 py-1.5 bg-accent-500 text-white text-xs font-medium rounded-lg"
                      >
                        {patient.room_id || patient.status === 'in_progress' ? 'Resume' : 'Start'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DoctorDashboard;
